import { motion } from 'framer-motion';
import { CheckCircle2, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ProfileCardProps {
  readonly children: React.ReactNode;
  readonly onClick?: () => void;
  readonly delay?: number;
  readonly hasHoverLift?: boolean;
  readonly hasGlowShadow?: boolean;
  readonly className?: string;
  readonly isComplete?: boolean;
  readonly showCompletionBadge?: boolean;
}

export function ProfileCard({ 
  children, 
  onClick, 
  delay = 0,
  hasHoverLift = true,
  hasGlowShadow = true,
  className,
  isComplete,
  showCompletionBadge = false
}: ProfileCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: delay * 0.5, duration: 0.2 }}
      whileHover={hasHoverLift ? { y: -2 } : undefined}
      onClick={onClick}
      className={cn(
        "rounded-2xl border transition-all duration-200 ease-out",
        "bg-[hsl(var(--v10-bg-surface))] border-[hsl(var(--v10-border))]",
        hasHoverLift && "cursor-pointer",
        hasHoverLift && "hover:border-[hsl(var(--v10-border-hover))] hover:bg-[hsl(var(--v10-bg-hover))]",
        hasGlowShadow && "hover:shadow-[0_0_20px_-8px_hsl(var(--v10-accent)/0.2)]",
        className
      )}
    >
      {children}
    </motion.div>
  );
}

interface ProfileCardIconProps {
  readonly icon: React.ReactNode;
  readonly isComplete?: boolean;
  readonly isPending?: boolean;
  readonly showCompletionBadge?: boolean;
}

export function ProfileCardIcon({ 
  icon, 
  isComplete, 
  isPending = false,
  showCompletionBadge = false 
}: ProfileCardIconProps) {
  return (
    <div className="relative">
      <div className={cn(
        "w-10 h-10 flex items-center justify-center rounded-lg transition-colors duration-200",
        "bg-[hsl(var(--v10-bg-elevated))]",
        "[&>svg]:w-5 [&>svg]:h-5 [&>svg]:transition-colors [&>svg]:duration-200",
        isPending 
          ? "group-hover:bg-pending/20 [&>svg]:text-white [&>svg]:group-hover:text-pending" 
          : "group-hover:bg-[hsl(var(--v10-accent)_/_0.2)] [&>svg]:text-white [&>svg]:group-hover:text-[hsl(var(--v10-accent))]"
      )}>
        {icon}
      </div>
      {showCompletionBadge && isComplete && (
        <div className="absolute -top-1 -right-1 animate-pulse-once">
          <CheckCircle2 className="w-4 h-4 text-[hsl(var(--v10-accent))] fill-[hsl(var(--v10-accent))] stroke-black" />
        </div>
      )}
    </div>
  );
}

interface ProfileCardStatusProps {
  isComplete: boolean;
  completeText?: React.ReactNode;
  incompleteText?: React.ReactNode;
}

export function ProfileCardStatus({ 
  isComplete, 
  completeText = 'Complété',
  incompleteText = 'En attente'
}: ProfileCardStatusProps) {
  return isComplete ? (
    <div className="flex items-center gap-1 text-xs text-[hsl(var(--v10-accent))]">
      <CheckCircle2 className="w-3 h-3 shrink-0" />
      {completeText}
    </div>
  ) : (
    <div className="flex items-center gap-1 text-xs text-pending">
      <AlertCircle className="w-3 h-3 shrink-0" />
      {incompleteText}
    </div>
  );
}

interface TeamAvatarButtonProps {
  readonly children: React.ReactNode;
  readonly onClick?: () => void;
  readonly delay?: number;
}

export function TeamAvatarButton({ 
  children, 
  onClick, 
  delay = 0 
}: TeamAvatarButtonProps) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: delay * 0.5, duration: 0.2 }}
      whileHover={{ y: -3, transition: { duration: 0.3 } }}
      whileTap={{ scale: 0.98, transition: { duration: 0.1 } }}
      onClick={onClick}
      className="flex flex-col items-center text-center group cursor-pointer"
    >
      {children}
    </motion.div>
  );
}
